// src/pages/Admin/Dashboard/VisitsChart.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../services/visitesServices";

const toYMD = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d || "");
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatFR = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d || "");
  return dt.toLocaleDateString("fr-FR");
};

const formatShortFR = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d || "");
  return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};

const VisitsChart = () => {
  const [data, setData] = useState([]);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Nouveau : mode d’affichage + fenêtre moyenne
  const [viewMode, setViewMode] = useState("daily"); // "daily" | "avg"
  const [avgWindow, setAvgWindow] = useState(7); // moyenne glissante sur X jours

  // Chargement des visites
  useEffect(() => {
    const fetchVisites = async () => {
      try {
        const response = await countVisitesByDate({
          include_internal: "0",
        });

        setData(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des visites :", error);
      }
    };

    fetchVisites();
  }, []);

  // ✅ Données normalisées + moyenne glissante
  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const normalized = data
      .map((item) => {
        const iso = toYMD(item.date_visite);
        const compteur = Number(item.compteur) || 0;
        return {
          ...item,
          date_iso: iso,
          date_label: formatShortFR(iso),
          date_full: formatFR(iso),
          compteur,
        };
      })
      .sort((a, b) => new Date(a.date_iso) - new Date(b.date_iso));

    const w = Math.max(1, Number(avgWindow) || 1);

    const withAvg = normalized.map((row, idx) => {
      const start = Math.max(0, idx - (w - 1));
      const slice = normalized.slice(start, idx + 1);
      const sum = slice.reduce(
        (acc, it) => acc + (Number(it.compteur) || 0),
        0,
      );
      const avg = slice.length ? sum / slice.length : 0;
      return { ...row, avg: Number(avg.toFixed(2)) };
    });

    return withAvg;
  }, [data, avgWindow]);

  // Stats dérivées
  const { totalVisits, lastDateLabel } = useMemo(() => {
    if (!Array.isArray(chartData) || chartData.length === 0) {
      return { totalVisits: 0, lastDateLabel: null };
    }
    const total = chartData.reduce(
      (sum, item) => sum + (Number(item.compteur) || 0),
      0,
    );
    const last = chartData[chartData.length - 1]?.date_iso;
    return {
      totalVisits: total,
      lastDateLabel: last ? new Date(last).toLocaleDateString("fr-FR") : null,
    };
  }, [chartData]);

  // Suppression des visites
  const handleDelete = async () => {
    if (!dateDebut || !dateFin) return;

    setLoading(true);
    try {
      await deleteVisitesByDate(dateDebut, dateFin);
      const updated = await countVisitesByDate({
        include_internal: "0",
      });
      setData(Array.isArray(updated) ? updated : []);
    } catch (error) {
      console.error("Erreur lors de la suppression des visites :", error);
    } finally {
      setLoading(false);
    }
  };

  const activeLine =
    viewMode === "daily"
      ? { key: "compteur", name: "Visites" }
      : { key: "avg", name: `Moyenne (${avgWindow}j)` };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 md:p-5 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 gap-3">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Trafic du site
          </h3>
          <p className="text-xs text-slate-500">
            Affichage :{" "}
            {viewMode === "daily"
              ? "visites par jour"
              : `moyenne glissante (${avgWindow} jours)`}
            .
          </p>

          {/* ✅ Nouveau : switch mode + fenêtre moyenne */}
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-1 w-fit">
              <button
                type="button"
                onClick={() => setViewMode("daily")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                  viewMode === "daily"
                    ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
                }`}
              >
                Visites
              </button>
              <button
                type="button"
                onClick={() => setViewMode("avg")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                  viewMode === "avg"
                    ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
                }`}
              >
                Moyenne
              </button>
            </div>

            {viewMode === "avg" && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Fenêtre :</span>
                <select
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                  value={avgWindow}
                  onChange={(e) => setAvgWindow(Number(e.target.value))}
                >
                  <option value={3}>3 jours</option>
                  <option value={7}>7 jours</option>
                  <option value={14}>14 jours</option>
                  <option value={30}>30 jours</option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="text-right text-xs text-slate-500">
          <div>
            Total cumulé :{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-100">
              {totalVisits}
            </span>
          </div>
          {lastDateLabel && <div>Dernière mesure : {lastDateLabel}</div>}
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 0 }}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="date_label"
              stroke="#64748b"
              tick={{ fontSize: 11 }}
            />
            <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value, name, props) => {
                // Affiche un libellé propre
                if (name === "compteur") return [value, "Visites"];
                if (name === "avg") return [value, `Moyenne (${avgWindow}j)`];
                return [value, name];
              }}
              labelFormatter={(_, payload) => {
                const row = payload?.[0]?.payload;
                return row?.date_full ? `Date : ${row.date_full}` : "";
              }}
              contentStyle={{
                backgroundColor: "#0f172a",
                border: "none",
                color: "#f9fafb",
                fontSize: 12,
              }}
              cursor={{
                stroke: "#94a3b8",
                strokeDasharray: "3 3",
              }}
            />
            <Legend wrapperStyle={{ color: "#475569", fontSize: 12 }} />

            {/* ✅ Une seule courbe selon le mode */}
            <Line
              type="monotone"
              dataKey={activeLine.key}
              name={activeLine.name}
              stroke={viewMode === "daily" ? "#0ea5e9" : "#22c55e"}
              activeDot={{ r: 6 }}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Zone nettoyage données */}
      <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800">
        <h4 className="text-sm font-semibold mb-1">
          Nettoyer l’historique des visites
        </h4>
        <p className="mb-3 text-xs text-slate-500">
          Pour alléger la base de données, vous pouvez supprimer des visites sur
          une plage de dates.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-center">
          <input
            type="date"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            value={dateDebut}
            onChange={(e) => setDateDebut(e.target.value)}
          />
          <input
            type="date"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            value={dateFin}
            onChange={(e) => setDateFin(e.target.value)}
          />
          <button
            className={`px-4 py-2 rounded-lg text-sm text-white ${
              loading ? "bg-slate-400" : "bg-rose-600 hover:bg-rose-700"
            }`}
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? "Suppression..." : "Supprimer les visites"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitsChart;
